import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLoginInfo from "hooks/useLoginInfo";
import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import { useLocation } from "react-router-dom";
import { renderStatusColor } from "utils/statusColor";
import Filter from "components/__new/common/Filter";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import Print from "components/__new/common/Print";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { TableContent } from "components/__new/common/TableContent";
import { DateFormat, TimeFormat } from "utils/dateFormat";
import CurrencyFormat from "utils/currencyFormat";
import Pagination from "components/__new/common/pagination";
import ContentLoading from "components/__new/common/ContentLoading";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import pluralize from "utils/pluralize";
import Maybe from "components/__new/common/Maybe";
import processParams from "utils/processParams";
import * as walletAction from "store/entities/wallet/action";
import useInlinePageRouter from "components/__new/general/useInlinePageRouter";
import { ReactComponent as IconDebit } from "assets/images/icons/project-icons/arrow-up-right.svg";
import { ReactComponent as IconCredit } from "assets/images/icons/project-icons/arrow-down-left.svg";
import TransactionDetailsWrapper from "components/__new/common/TransactionDetailsWrapper";
import { truncate } from "utils/truncate";
import useBanks from "hooks/useBanks";

export const TransactionHistory = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const { InlineRouterTab } = useInlinePageRouter();
    const { transactionsLoading, transactions, transactionsDownloadLoading, transactionsDownload } = useSelector((s) => s.entities.wallet);
    const { banks, banksLoading } = useBanks(userInfo?.token);


    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [isOpen, setIsOpen] = useState(false);
    const [transaction, setTransaction] = useState({});
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });


    // const getTransactionsCount = () => {
    //     let params = {...listParams};

    //     params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
    //     params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

    //     dispatch(walletAction.getTransactionsCount(userInfo.token, params));
    // }


    const getTransactions = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");
        params = processParams(urlQueryParams, params, "search", "string", "", "keyword");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");
        params = processParams(urlQueryParams, params, "type", "string", "", "type");

        setListParams(params);
        dispatch(walletAction.getTransactions(userInfo.token, params));
    }


    const viewDetails = (transaction) => {
        setTransaction(transaction);
        setIsOpen(true);
    }


    useEffect(() => {
        // getTransactionsCount();
        getTransactions();
        // eslint-disable-next-line
    }, [location]);
    
    const queryOptions = [
        {
            title: "All",
            type: "query",
            queryName: "status",
            queryValue: "",
        },
        {
            title: "Successful",
            type: "query",
            queryName: "status",
            queryValue: "successful",
        },
        {
            title: "Pending",
            type: "query",
            queryName: "status",
            queryValue: "pending",
        },
        {
            title: "Failed",
            type: "query",
            queryName: "status",
            queryValue: "failed",
        },
    ]


    return (
        <div>

            <DynamicHeadTag
                headerText="Transaction History"
            />

            <TransactionDetails
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                transaction={transaction}
                banks={banks}
            />

            <div className="lg:flex">
                <div className="page-title">
                    Transaction History
                </div>
            </div>

            <div className="mt-5">
                <InlineRouterTab
                    queryMode={true}
                    items={queryOptions}
                />
            </div>

            <div className="mt-6 box">
                <div className="flex items-center justify-start w-max space-x-3">
                    <div className="font-medium">Transaction History</div>
                    <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                        <ContentLoading
                            loading={transactionsLoading || banksLoading}
                            data={<>{transactions?.data?.totalDocs ?? "-"} {pluralize(transactions?.data?.totalDocs, "transaction", "transactions")}</>}
                        />
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap justify-between">
                        <>
                            <div className="max-w-sm flex-grow sm:mr-6">
                                <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                            </div>
                            <div className="flex flex-wrap space-x-3 sm:space-x-3">
                                <div>
                                    <Filter filters={[
                                        {
                                            title: "Date range",
                                            name: "dateRange",
                                            dataType: "custom-timeframe",
                                            hasResetButton: true,
                                            options: [
                                                {
                                                    display: "Today",
                                                    payload: "today",
                                                },
                                                {
                                                    display: "Last 7 days",
                                                    payload: "7days",
                                                },
                                                {
                                                    display: "30 days",
                                                    payload: "30days",
                                                },
                                                {
                                                    display: "1 Year",
                                                    payload: "1year",
                                                },
                                            ],
                                        },
                                        {
                                            title: "Custom Date Range",
                                            dataType: "date-range",
                                            options: {
                                                from: {
                                                    name: "startDate",
                                                    placeholder: "Start Date",
                                                },
                                                to: {
                                                    name: "endDate",
                                                    placeholder: "End Date",
                                                },
                                            },
                                        },
                                        {
                                            title: "Status",
                                            name: "status",
                                            dataType: "select",
                                            options: [
                                                {
                                                    display: "All",
                                                    payload: "",
                                                },
                                                {
                                                    display: "Successful",
                                                    payload: "successful",
                                                },
                                                {
                                                    display: "Pending",
                                                    payload: "pending",
                                                },
                                                {
                                                    display: "Failed",
                                                    payload: "failed",
                                                },
                                            ],
                                        },
                                        {
                                            title: "Type",
                                            name: "type",
                                            dataType: "select",
                                            options: [
                                                {
                                                    display: "Credit",
                                                    payload: "credit",
                                                },
                                                {
                                                    display: "Debit",
                                                    payload: "debit",
                                                },
                                            ],
                                        },
                                    ]}/>
                                </div>

                                <div>
                                    <DownloadAsCSV
                                        ref={downloadAsCSVRef}
                                        tooltip="Download transactions list"
                                        filename="Transactions.csv"
                                        columns={[
                                            { label: "Transaction Date", key: "createdAt" },
                                            { label: "Transaction Type", key: "type" },
                                            { label: "Source Type", key: "sourceType" },
                                            { label: "Target", key: "target" },
                                            { label: "Previous Balance", key: "previousBalance" },
                                            { label: "Amount", key: "amount" },
                                            { label: "Current Balance", key: "newBalance" },
                                            { label: "Reference", key: "reference" },
                                            { label: "Transaction Reference", key: "transactionRef" },
                                            { label: "Approval Status", key: "status" },
                                        ]}
                                        click={() => {
                                            dispatch(walletAction.getTransactionsDownload(userInfo.token, {...listParams, page: 1, limit: 10000})).then(() => {
                                                downloadAsCSVRef.current?.downloadData();
                                            });
                                        }}
                                        loading={transactionsDownloadLoading}
                                        response={transactionsDownload?.data?.docs}
                                    />
                                </div>

                                <div>
                                    <Print component={printComponentRef} />
                                </div>
                            </div>
                        </>
                </div>
            </div>

                <div className="bg-white">
                    <div ref={printComponentRef}>

                        <div className="mb-4s text-xl hidden print:block">
                            Transactions
                        </div>

                        <Table classTable="table-hover">
                            <Table.Head>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Transaction</th>
                                <th>Narration</th>
                                <th>Amount</th>
                                <th>Status</th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={transactionsLoading}
                                    loadingText="Loading transactions"
                                    data={transactions?.data}
                                    colspan={6}
                                    emptyUI={<>
                                        <div className="py-20 mx-auto text-center text-base">
                                            <div className="flex justify-center">
                                                <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                            </div>
                                            <div className="text-[#101828] font-medium">
                                                No Data Available Yet
                                            </div>
                                            <div className="mt-1 text-[#4A5564]">
                                                Once you start transacting, it will appear here
                                            </div>
                                        </div>
                                    </>}
                                />

                                {transactionsLoading === false && transactions?.data?.docs?.map((transaction, index) => {
                                    const isDebit = transaction?.type === 'debit'

                                    return (
                                        <tr key={index} onClick={() => viewDetails(transaction)}>
                                            <td style={{ width: '180px' }}>
                                                <div>
                                                    <div className="text-[#101828] mb-1">{DateFormat(transaction?.createdAt)}</div>
                                                    <div>{TimeFormat(transaction?.createdAt)}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="capitalize flex items-center">
                                                    <div className={`w-8 h-8 mr-3 flex rounded-full flex-shrink-0 ${isDebit ? "bg-[#FEF2F2]" : "bg-[#cfffe3]"}`}>
                                                        {isDebit ? <IconDebit className="m-auto" /> : <IconCredit className="m-auto" />}
                                                    </div>
                                                    {transaction.type}
                                                </div>
                                            </td>
                                            <td style={{ width: '200px' }}>
                                                {/* <div className="mb-1 text-[#101828]">Airtime Purchase</div> */}
                                                <div className="capitalize">{truncate(transaction?.description, 30)}</div>
                                            </td>
                                            <td className="capitalize" style={{ width: '200px' }}>
                                                {truncate(transaction?.narration || transaction?.description, 30) || "-"}
                                            </td>
                                            <td className="!text-[#101828] font-medium" style={{ width: '200px' }}>
                                                {CurrencyFormat(transaction?.amount / 100 || 0)}
                                            </td>
                                            <td>
                                                <div className={"label capitalize " + renderStatusColor(transaction?.status)}>
                                                    {transaction?.status}
                                                </div>
                                            </td>
                                    </tr>
                                    )
                                })}

                            </Table.Body>
                        </Table>

                        <Maybe condition={transactionsLoading === false && transactions?.data?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={transactions?.data} />
                            </div>
                        </Maybe>

                    </div>
                </div>

        </div>
    )
}


const TransactionDetails = ({transaction, setIsOpen, isOpen, banks}) => {
    const isCredit = transaction?.type === 'credit';
    const isDebit = transaction?.type === 'debit';
    const transactionAmount = `${isCredit ? '' : ''}${CurrencyFormat(transaction?.amount / 100)}`
    
    const bankName = useMemo(() => {
        if (banks?.length === 0 || !transaction?.senderBankCode) return "";
        
        const bankDetails = banks.find((bank) => +(bank?.bankCode || bank?.institutionCode) === +transaction?.senderBankCode);
        return (bankDetails?.bankName || bankDetails?.institutionName) || "";
    }, [banks, transaction]);
        
    const debitPad = () => {
        if (isDebit && transaction?.product === "airtime") {
            return "84px";
        } else if (isDebit && transaction?.product === "data") {
            return "84px";
        } else if (isDebit && transaction?.product === "electricity") {
            return "10px";
        } else if (isDebit && transaction?.product === "tv") {
            return "108px";
        } else if (isDebit && transaction?.product === "general") {
            return "36px";
        } else if (isDebit && transaction?.product === "top-up") {
            return "132px";
        } else if (isDebit && transaction?.product === "charge") {
            return "108px";
        } else if (isDebit) {
            return "108px";
        } else {
            return "";
        }
    }
    
    const creditPad = () => {
        if (isCredit && transaction?.product === "airtime") {
            return "36px";
        } else if (isCredit && transaction?.product === "data") {
            return "36px";
        } else if (isCredit && transaction?.product === "electricity") {
            return "36px";
        } else if (isCredit && transaction?.product === "tv") {
            return "36px";
        } else if (isCredit && transaction?.product === "general") {
            return "36px";
        }  else if (isCredit && transaction?.product === "top-up") {
            return "132px";
        }  else if (isCredit && transaction?.product === "charge") {
            return "108px";
        } else if (isCredit) {
            return "36px";
        } else {
            return "";
        }
    }
    
    // const debitPad = isDebit ? "" : "108px";
    // const creditPad = isCredit ? "" : "36px";

    return (
        <TransactionDetailsWrapper
            title={transaction?.type}
            subtitle={
                `${transaction?.product?.charAt(0).toUpperCase()}${transaction?.product?.substring(1)}`
            }
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            amount={transactionAmount}
            status={transaction?.status}
            isCredit={isCredit}
            padFooter={isDebit ? debitPad() : isCredit ? creditPad() : ""}
            documentTitle={`Transfer Receipt - ${DateFormat(transaction?.createdAt)}`}>
                {
                    transaction?.reference &&  (
                        <>
                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Reference</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.reference ?? "-"}
                                </div>
                            </div>
                        </>
                    )
                }

                <div className="w-full flex items-center justify-between text-sm mt-7">
                    <div className="text-ep-gray-500">Time</div>
                    <div className="text-ep-gray-700">
                        {DateFormat(transaction?.createdAt)} at {TimeFormat(transaction?.createdAt)}
                    </div>
                </div>

                {
                    transaction?.product === "general" && transaction?.type === "credit" && (
                        <>
                            {/* Credits with product general are funds coming in and should have a senderAccountNumber if it doesn't then it's probably a reversal and would have destinationAccountNumber  */}
                            <Maybe condition={transaction?.senderAccountNumber}>
                                <div className="w-full flex items-center justify-between text-sm mt-7">
                                    <div className="text-ep-gray-500">Sender Bank Name</div>
                                    <div className="text-ep-gray-700">
                                        {bankName || "N/A"}
                                    </div>
                                </div>

                                <div className="w-full flex items-center justify-between text-sm mt-7">
                                    <div className="text-ep-gray-500">Sender Account Number</div>
                                    <div className="text-ep-gray-700">
                                        {transaction?.senderAccountNumber || "N/A"}
                                    </div>
                                </div>

                                <div className="w-full flex items-center justify-between text-sm mt-7">
                                    <div className="text-ep-gray-500">Sender Account Name</div>
                                    <div className="text-ep-gray-700">
                                        {transaction?.senderAccountName || "N/A"}
                                    </div>
                                </div>
                            </Maybe>

                            {
                                transaction?.sessionId && (
                                    <>
                                        <div className="w-full flex items-center justify-between text-sm mt-7">
                                            <div className="text-ep-gray-500">Session ID</div>
                                            <div className="text-ep-gray-700">
                                                {transaction?.sessionId || "N/A"}
                                            </div>
                                        </div>
                                    </>
                                )
                            }
                        </>
                    )
                }

                {
                    transaction?.product === "general" && transaction?.type === "debit" && (
                        <>
                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Destination Bank Name</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.destinationBankName || "N/A"}
                                </div>
                            </div>

                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Destination Account Number</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.destinationAccountNumber || "N/A"}
                                </div>
                            </div>

                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Destination Account Name</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.destinationAccountName || "N/A"}
                                </div>
                            </div>

                            {
                                transaction?.sessionId && (
                                    <>
                                        <div className="w-full flex items-center justify-between text-sm mt-7">
                                            <div className="text-ep-gray-500">Session ID</div>
                                            <div className="text-ep-gray-700">
                                                {transaction?.sessionId || "N/A"}
                                            </div>
                                        </div>
                                    </>
                                )
                            }
                        </>
                    )
                }

                {
                    transaction?.sourceType === "bill-payment" && transaction?.product === "electricity" && (
                        <>
                            {transaction?.providerResponse?.token && <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Token</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.providerResponse?.token}
                                </div>
                            </div>}

                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Meter Number</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.providerResponse?.meterNumber}
                                </div>
                            </div>

                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Account Name</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.providerResponse?.name}
                                </div>
                            </div>

                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Meter Type</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.providerResponse?.meterType}
                                </div>
                            </div>

                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Address</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.providerResponse?.address}
                                </div>
                            </div>
                        </>
                    )
                }

                {
                    transaction?.sourceType === "bill-payment" && transaction?.product === "data" && (
                        <>
                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Phone</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.providerResponse?.phone}
                                </div>
                            </div>
                        </>
                    )
                }

                {
                    transaction?.sourceType === "bill-payment" && transaction?.product === "airtime" && (
                        <>
                            <div className="w-full flex items-center justify-between text-sm mt-7">
                                <div className="text-ep-gray-500">Phone</div>
                                <div className="text-ep-gray-700">
                                    {transaction?.providerResponse?.phone}
                                </div>
                            </div>
                        </>
                    )
                }
        </TransactionDetailsWrapper>
    )
}


export default TransactionHistory;